import { createClient } from '@supabase/supabase-js'

// Prefer Vite env vars, but fall back to the current Supabase project details.
// The anon key is safe to ship in the client (it's a public key), but in production
// you should still set Netlify env vars so you can rotate/change without code edits.
// Note: Empty strings in env vars will override fallbacks, so we check for truthy values
const envUrl = import.meta.env.VITE_SUPABASE_URL
const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Detect if old project is being used (paused/deleted)
const OLD_PROJECT_REF = 'ibfzrwmpbgcqcifpqbhj'
const NEW_PROJECT_REF = 'kdazseyrfzqersfufyej'
const NEW_PROJECT_URL = 'https://kdazseyrfzqersfufyej.supabase.co'
const NEW_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtkYXpzZXlyZnpxZXJzZnVmeWVqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjkxNzU0MjUsImV4cCI6MjA4NDc1MTQyNX0.fsrnhBSpbhiL9fxC3E9LDSDWghAS9qukXGOzqFp1xXY'

let supabaseUrl = (envUrl && envUrl.trim() !== '') 
  ? envUrl 
  : NEW_PROJECT_URL

let supabaseAnonKey = (envKey && envKey.trim() !== '') 
  ? envKey 
  : NEW_ANON_KEY

// Auto-fix: If old project detected, use new project instead
if (supabaseUrl.includes(OLD_PROJECT_REF)) {
  console.warn('⚠️ Old Supabase project detected! Auto-switching to new project.')
  console.warn('⚠️ Please update Netlify env vars: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY')
  supabaseUrl = NEW_PROJECT_URL
  supabaseAnonKey = NEW_ANON_KEY
}

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase configuration:', { supabaseUrl, supabaseAnonKey: supabaseAnonKey ? 'SET' : 'MISSING' })
  throw new Error('Missing Supabase configuration (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)')
}

// Validate URL format
if (!supabaseUrl.startsWith('http://') && !supabaseUrl.startsWith('https://')) {
  console.error('❌ Invalid Supabase URL format:', supabaseUrl)
  throw new Error('Invalid Supabase URL format - must start with http:// or https://')
}

// Debug logging (remove in production if desired)
console.log('🔧 Supabase Config:', {
  url: supabaseUrl,
  urlType: typeof supabaseUrl,
  urlLength: supabaseUrl?.length,
  keyPrefix: supabaseAnonKey?.substring(0, 20) + '...',
  keyLength: supabaseAnonKey?.length,
  usingEnvVars: !!import.meta.env.VITE_SUPABASE_URL,
  envUrl: import.meta.env.VITE_SUPABASE_URL || 'NOT SET'
})
console.log('🔧 Full Supabase URL:', supabaseUrl)
console.log('🔧 Full Anon Key (first 50 chars):', supabaseAnonKey?.substring(0, 50))

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storageKey: 'fundamentals-auth',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
})

export const authHelpers = {
  async signUp(email, password, userData = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: userData }
    })
    if (error) throw error
    return { data, error: null }
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return { data, error: null }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    return { error: null }
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return { user, error: null }
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

export default supabase
